/**
 * Broker endpoints (real backend `/v2/brokers/` — formerly `/v1/agent/`).
 *
 * Brokers ("agents" in the old v1 model) are the *people* that broker
 * for a brokerage company. The Brokerage section's selector lists
 * brokers — the selected broker's id is what `submission.broker_id`
 * stores. When a broker is selected the form pulls broker name + email
 * from the broker row, and brokerage name + address from the embedded
 * `brokerage` relation that the backend includes in the response.
 */
import type { AgentCreateInput, AgentRow } from "../../types/submission";
import { getJsonReal, sendJsonReal } from "../client";

export interface ListAgentsParams {
    search?: string;
    /** Restrict results to a specific brokerage (used by the modal flow
     *  when creating a new agent under a known brokerage). */
    brokerage_id?: string;
    limit?: number;
    offset?: number;
}

export function listAgents(params: ListAgentsParams = {}): Promise<AgentRow[]> {
    const qs = new URLSearchParams();
    if (params.search) qs.set("search", params.search);
    if (params.brokerage_id) qs.set("brokerage_id", params.brokerage_id);
    qs.set("limit", String(params.limit ?? 50));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    return getJsonReal<AgentRow[]>(`/v2/brokers/?${qs.toString()}`);
}

export function getAgent(id: string): Promise<AgentRow> {
    return getJsonReal<AgentRow>(`/v2/brokers/${encodeURIComponent(id)}`);
}

export function createAgent(input: AgentCreateInput): Promise<AgentRow> {
    return sendJsonReal<AgentRow>("/v2/brokers/", "POST", input);
}

/** Compose a single-line brokerage address from the agent's embedded
 *  brokerage relation. Empty parts are skipped so a partial brokerage
 *  doesn't leave dangling commas. */
export function formatAgentBrokerageAddress(agent: AgentRow): string {
    const b = agent.brokerage;
    if (!b) return "";
    return [b.address, b.city, b.state, b.zip_code].filter((s) => s && s.trim() !== "").join(", ");
}
